import React, { useEffect, useState, useMemo } from 'react';
import styles from './FilterManagementView.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import Dropdown from 'react-bootstrap/Dropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { setSearchText, setSelectedGroup, setSelectedLocation, setSelectedTeam, setSelectedOrganization, setGroup, setLocation, setTeam, setFilterState } from '../slicers/FilterSlice';
import { closeModal, openModal } from './PerinHealthModelSlice';
import PerinHealthModal from './PerinHealthModel';
import inputStyles from '../views/ManagementPages.module.css';
import Logger from '../services/Logger';
import { LogElements, LogAction } from '../services/LogElements';

const FilterManagementView = (props) => {
    const {
        addButtonLabel,
        onAddHandler,
        onDeleteHandler,
        isOrganization,
        isGroup,
        isSubGroup,
        isTeam,
        isSortBy,
        dropdownOptions = [],
        modelHeader,
        showDeleteButton = true,
        showAddButton = true,
        customDeleteModal,
        showReactivateButton = false,
        onReactivateHandler = null,
        showArchiveButton = false,
        onArchiveHandler = null,
        archiveModalHeader = "Archive Account?",
        deleteButtonLabel = "Delete",
        showInpatientFilters = false
    } = props;

    const dispatch = useDispatch();
    const {
        groups,
        locations,
        teams,
        inpatientLocationHierarchy,
        selectedGroup,
        selectedLocation,
        selectedTeam,
        selectedOrganization,
        selectedInpatientFloor,
        selectedInpatientLocation,
        selectedInpatientRoom
    } = useSelector((state) => state.filters);

    const perinHealthGrid = useSelector((state) => state.perinHealthGrid);
    const deleteDisabled = perinHealthGrid.selectedRows.length <= 0;
    const addDisabled = perinHealthGrid.selectedRows.length > 0;
    const reactivateDisabled = perinHealthGrid.selectedRows.length <= 0;
    const archiveDisabled = perinHealthGrid.selectedRows.length <= 0;
    const userGroups = useSelector((state) => state.userInfo.organizationsHierarchy);
    const isAdmin = useSelector((state) => state.userInfo.isAdmin);
    const roleCategory = useSelector((state) => state.userInfo.roleCategory);

    const [defaultOrganizationId] = userGroups.map((organization) => organization.organizationId);



    const initDropdowns = (allGroups) => {
        const defaultGroupId = allGroups[0].groupId;
        const selectedGroup = allGroups.find(group => group.groupId === defaultGroupId);
        if (selectedGroup && selectedGroup.subgroups && selectedGroup.subgroups.length > 0 && isSubGroup) {
            const allSubgroups = selectedGroup.subgroups;
            let dispatchingSubGroups = allSubgroups;
            if (allSubgroups.length > 1) {
                dispatchingSubGroups = [{ subGroupId: '', subGroupName: 'All' }, ...allSubgroups];
            }
            dispatch(setSelectedLocation(allSubgroups.length > 1 ? "" : allSubgroups[0].subGroupId));
            dispatch(setLocation(dispatchingSubGroups));

            if (allSubgroups.length === 1 && allSubgroups[0].teams && allSubgroups[0].teams.length > 0) {
                const allTeams = allSubgroups[0].teams;
                let dispatchingTeams = allTeams;
                if (allTeams.length > 1) {
                    dispatchingTeams = [{ teamId: '', teamName: 'All' }, ...allTeams];
                }
                dispatch(setTeam(dispatchingTeams));

                const defaultTeamId = allTeams[0].teamId;
                dispatch(setSelectedTeam(allTeams.length > 1 ? "" : defaultTeamId));
            }
        }
    };

    useEffect(() => {
        if (!isAdmin && userGroups.length === 1) {
            // only one organization
            const organization = userGroups.find((org) => org.organizationId === Number(defaultOrganizationId));
            if (organization && organization.groups.length > 0) {
                const allGroups = [...organization.groups];
                const isGroupAdminWithMultipleGroups = roleCategory === 'Admin' && allGroups.length > 1;
                if (isGroupAdminWithMultipleGroups) {
                    dispatch(setGroup([{ groupId: '', groupName: 'All' }, ...allGroups]));
                    dispatch(setSelectedGroup(''));
                    dispatch(setLocation([{ subGroupId: '', subGroupName: 'All' }]));
                    dispatch(setSelectedLocation(''));
                    dispatch(setTeam([{ teamId: '', teamName: 'All' }]));
                    dispatch(setSelectedTeam(''));
                } else {
                    dispatch(setGroup(allGroups));
                    initDropdowns(allGroups);
                }
            }
        } else if (!isAdmin && userGroups.length > 1) {
            // multiple organizations
            const allGroups = [];
            userGroups.forEach((org) => {
                allGroups.push(...org.groups);
            })
            dispatch(setGroup([{ groupId: '', groupName: 'All' }, ...allGroups]));
        } else {
            // user is admin, all other dropdowns will be displayed on org selection
            dispatch(setSearchText(''));
            dispatch(setGroup([]));
            dispatch(setLocation([]));
            dispatch(setTeam([]));
        }
    }, [isAdmin, defaultOrganizationId, dispatch, roleCategory, userGroups.length]);

    const handleSearchChange = (e) => {
        dispatch(setSearchText(e.target.value));
    };

    const handleOrganizationChange = (e) => {
        const value = e.target.value;
        dispatch(setSelectedOrganization(value));
        dispatch(setSelectedGroup(''));
        dispatch(setSelectedLocation(''));
        dispatch(setSelectedTeam(''));
        dispatch(setLocation([]));
        dispatch(setTeam([]));

        const organization = userGroups.find((org) => org.organizationId === Number(e.target.value));
        if (organization && organization.groups.length > 0) {
            const allGroups = [...organization.groups];
            dispatch(setGroup(allGroups));

            if (allGroups.length === 1) {
                initDropdowns(allGroups);
            } else {
                dispatch(setGroup([{ groupId: '', groupName: 'All' }, ...organization.groups]));
            }
        } else {
            dispatch(setGroup([]));
        }
    };



    const handleGroupChange = (e) => {
        const value = e.target.value;
        dispatch(setSelectedGroup(value));
        dispatch(setSelectedLocation(''));
        dispatch(setSelectedTeam(''));
        dispatch(setLocation([{ subGroupId: '', subGroupName: 'All' }]));
        dispatch(setTeam([{ teamId: '', teamName: 'All' }]));

        const selectedGroup = groups?.find((group) => group.groupId === Number(e.target.value));
        if (selectedGroup && selectedGroup.subgroups && selectedGroup.subgroups.length > 0) {
            const allSubgroups = selectedGroup.subgroups;
            let dispatchingSubGroups = allSubgroups;
            if (allSubgroups.length > 1) {
                dispatchingSubGroups = [{ subGroupId: '', subGroupName: 'All' }, ...allSubgroups];
            }
            dispatch(setLocation(dispatchingSubGroups));
            if (allSubgroups.length === 1 && isSubGroup) {
                const selectedSubgroup = allSubgroups.find((subgroup) => subgroup.subGroupId === Number(allSubgroups[0].subGroupId));
                if (selectedSubgroup && selectedSubgroup.teams && selectedSubgroup.teams.length > 0) {
                    const allTeams = [{ teamId: '', teamName: 'All' }, ...selectedSubgroup.teams];
                    dispatch(setTeam(allTeams));
                }
            }

        }
    };

    const handleLocationChange = (e) => {
        const value = e.target.value;
        dispatch(setSelectedLocation(value));
        dispatch(setSelectedTeam(''));
        dispatch(setTeam([{ teamId: '', teamName: 'All' }]));

        const selectedSubgroup = locations?.find((subgroup) => subgroup.subGroupId === Number(e.target.value));
        if (selectedSubgroup && selectedSubgroup.teams && selectedSubgroup.teams.length > 0) {
            const allTeams = [{ teamId: '', teamName: 'All' }, ...selectedSubgroup.teams];
            dispatch(setTeam(allTeams));
        }
    };

    const handleTeamChange = (e) => {
        const value = e.target.value;
        dispatch(setSelectedTeam(value));
    };

    const handleSortByChange = () => {
        // dispatch(setSelectedTeam(e.target.value));
    };

    const onAddClickHandler = () => {
        if (addButtonLabel === 'Enroll Patient') {
            Logger.shared.info(
                LogElements.enrollPatientButton,
                LogAction.clicked,
                'FilterManagementView.js',
                'Start'
            );
        }
        onAddHandler();
    };

    const onReactivateClickHandler = () => {
        if (onReactivateHandler) {
            onReactivateHandler();
        }
    };

    const inpatientLocations = useMemo(() => {
        if (!Array.isArray(inpatientLocationHierarchy) || inpatientLocationHierarchy.length === 0) {
            return [];
        }
        if (selectedOrganization) {
            return inpatientLocationHierarchy
            .filter((item) => item.organizationId === Number(selectedOrganization))
            .map((item) => ({
                locationId: item.location,
                locationName: item.location
            }));;
        } else {
            return inpatientLocationHierarchy.map((item) => ({
                locationId: item.location,
                locationName: item.location
            }));
        }
    }, [inpatientLocationHierarchy, selectedOrganization]);

    const inpatientFloors = useMemo(() => {
        if (!selectedInpatientLocation || !Array.isArray(inpatientLocationHierarchy) || inpatientLocationHierarchy.length === 0) {
            return [];
        }
        const selectedLocationData = inpatientLocationHierarchy.find(
            (item) => item.location === selectedInpatientLocation
        );
        if (!selectedLocationData || !Array.isArray(selectedLocationData.floors)) {
            return [];
        }
        return selectedLocationData.floors.map((floor) => ({
            floorId: floor.floor,
            floorName: floor.floor
        }));
    }, [selectedInpatientLocation, inpatientLocationHierarchy]);

    const inpatientRooms = useMemo(() => {
        if (!selectedInpatientLocation || !selectedInpatientFloor || !Array.isArray(inpatientLocationHierarchy) || inpatientLocationHierarchy.length === 0) {
            return [];
        }
        const selectedLocationData = inpatientLocationHierarchy.find(
            (item) => item.location === selectedInpatientLocation
        );
        if (!selectedLocationData || !Array.isArray(selectedLocationData.floors)) {
            return [];
        }
        const selectedFloorData = selectedLocationData.floors.find(
            (floor) => floor.floor === selectedInpatientFloor
        );
        if (!selectedFloorData || !Array.isArray(selectedFloorData.rooms)) {
            return [];
        }
        return selectedFloorData.rooms.map((room) => ({
            roomId: room.room,
            roomName: room.room
        }));
    }, [selectedInpatientLocation, selectedInpatientFloor, inpatientLocationHierarchy]);

    const handleInpatientLocationChange = (e) => {
        const value = e.target.value;
        dispatch(setFilterState({'key': 'selectedInpatientLocation', 'value': value}));
        dispatch(setFilterState({'key': 'selectedInpatientFloor', 'value': ''}));
        dispatch(setFilterState({'key': 'selectedInpatientRoom', 'value': ''}));
    };

    const handleInpatientFloorChange = (e) => {
        const value = e.target.value;
        dispatch(setFilterState({'key': 'selectedInpatientFloor', 'value': value}));
        dispatch(setFilterState({'key': 'selectedInpatientRoom', 'value': ''}));
    };

    const handleInpatientRoomChange = (e) => {
        const value = e.target.value;
        dispatch(setFilterState({'key': 'selectedInpatientRoom', 'value': value}));
    };

    const onArchiveClickHandler = () => {
        dispatch(
            openModal({
                activeModalId: "FilterManagementViewArchive",
                header: archiveModalHeader,
                message: `Once archived, the account will be removed from the system and will no longer be visible in any account lists.<br /><span style="color: #dc3545; font-weight: bold;">This action cannot be undone.</span>`,
                buttons: [
                    { label: "Archive", variant: 'primary', onClickKey: 'YES' },
                    { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
                ],
            })
        );
    };

    

    const handleDialogButtonClick = (onClickKey) => {
        if (onClickKey === 'YES') {
            onDeleteHandler();
        } else {
            dispatch(closeModal());
        }
    };

    const handleArchiveDialogButtonClick = (onClickKey) => {
        if (onClickKey === 'YES') {
            if (onArchiveHandler) {
                onArchiveHandler();
            }
            dispatch(closeModal());
        } else {
            dispatch(closeModal());
        }
    };

    const onDeleteRowHandler = () => {
        if (customDeleteModal) {
            customDeleteModal();
        } else {
            dispatch(
                openModal({
                    activeModalId: "FilterManagementView",
                    header: modelHeader,
                    message: `Do you want to ${deleteButtonLabel.toLowerCase()} the selected record?`,
                    buttons: [
                        { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
                        { label: deleteButtonLabel, variant: 'primary', onClickKey: 'YES' },
                    ],
                })
            );
        }

    };

    return (
        <div className={styles.FilterManagmentView}>
            <div className={`${styles.LeftSection} col-1`}>
                <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
                    <div style={{ position: "absolute", paddingLeft: "10px" }}>
                        <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" size="xs" style={{ color: "D9D9D9" }} />
                    </div>
                    <input
                        type="text"
                        placeholder="Search..."
                        onChange={handleSearchChange}
                        className={styles.SearchInput}
                    />
                </div>
            </div>

            <div className={`${styles.RightSection} col-11 justify-content-end`}>
                {isAdmin && isOrganization && (
                    <div style={{ position: "relative" }}>
                        <span className="floating-label">Organization</span>
                        <select
                            className={inputStyles["styled-input-select"]}
                            onChange={handleOrganizationChange}
                        >
                            <option value="">Select</option>
                            {userGroups.map((organization) => (
                                <option key={organization.organizationId} value={organization.organizationId}>
                                    {organization.organizationName}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                {isGroup && (
                    <div style={{ position: "relative" }}>
                        <span className="floating-label">Group</span>
                        <select
                            className={inputStyles["styled-input-select"]}
                            onChange={handleGroupChange}
                            value={selectedGroup}
                        >
                            {groups?.map((group) => (
                                <option key={group.groupId} value={group.groupId}>
                                    {group.groupName}
                                </option>
                            ))}
                        </select>
                    </div>)
                }


                {isSubGroup && (
                    <div style={{ position: "relative" }}>
                        <span className="floating-label">Location</span>
                        <select
                            className={inputStyles["styled-input-select"]}
                            onChange={handleLocationChange}
                            value={selectedLocation}
                        >
                            {locations?.map((subgroup) => (
                                <option key={subgroup.subGroupId} value={subgroup.subGroupId}>
                                    {subgroup.subGroupName}
                                </option>
                            ))}
                        </select>
                    </div>)
                }
                {isTeam && (
                    <div style={{ position: "relative" }}>
                        <span className="floating-label">Team</span>
                        <select
                            className={inputStyles["styled-input-select"]}
                            onChange={handleTeamChange}
                            value={selectedTeam}
                        >
                            {teams.map((team) => (
                                <option key={team.teamId} value={team.teamId}>
                                    {team.teamName}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                {showInpatientFilters &&
                    <>
                        <div style={{ position: "relative" }}>
                            <span className="floating-label">Location</span>
                            <select
                                className={inputStyles["styled-input-select"]}
                                onChange={handleInpatientLocationChange}
                                value={selectedInpatientLocation || ''}
                            >
                                <option value="">All</option>
                                {inpatientLocations.map((location) => (
                                    <option key={location.locationId} value={location.locationId}>
                                        {location.locationName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{ position: "relative" }}>
                            <span className="floating-label">Floor</span>
                            <select
                                className={inputStyles["styled-input-select"]}
                                onChange={handleInpatientFloorChange}
                                value={selectedInpatientFloor || ''}
                                disabled={!selectedInpatientLocation || inpatientFloors.length === 0}
                            >
                                <option value="">All</option>
                                {inpatientFloors.map((floor) => (
                                    <option key={floor.floorId} value={floor.floorId}>
                                        {floor.floorName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{ position: "relative" }}>
                            <span className="floating-label">Room</span>
                            <select
                                className={inputStyles["styled-input-select"]}
                                onChange={handleInpatientRoomChange}
                                value={selectedInpatientRoom || ''}
                                disabled={!selectedInpatientFloor || inpatientRooms.length === 0}
                            >
                                <option value="">All</option>
                                {inpatientRooms.map((room) => (
                                    <option key={room.roomId} value={room.roomId}>
                                        {room.roomName}
                                    </option>
                                ))}
                            </select>
                        </div>

                    </>
                }

                {isSortBy && (
                    <div style={{ position: "relative" }}>
                        <span className="floating-label">Sort By</span>
                        <select
                            className={inputStyles["styled-input-select"]}
                            onChange={handleSortByChange}
                        >
                            <option value="name">Name</option>
                            <option value="date">Date</option>
                            <option value="rating">Rating</option>
                        </select>
                    </div>
                )}
                {showDeleteButton &&
                    <Button
                        variant="secondary"
                        disabled={deleteDisabled}
                        className={deleteDisabled ? styles.DeleteButtonDisabled : styles.DeleteButton}
                        onClick={onDeleteRowHandler}
                    >
                        <FontAwesomeIcon icon="fa-regular fa-trash-can" style={{ color: deleteDisabled ? '#B9B9B9' : 'black' }} />
                        {deleteButtonLabel}
                    </Button>
                }
                {showReactivateButton &&
                    <Button
                        variant="secondary"
                        disabled={reactivateDisabled}
                        className={reactivateDisabled ? styles.DeleteButtonDisabled : styles.DeleteButton}
                        onClick={onReactivateClickHandler}
                    >
                        <FontAwesomeIcon icon="fa-solid fa-arrows-rotate" style={{ color: reactivateDisabled ? '#B9B9B9' : 'black' }} />
                        Reactivate
                    </Button>
                }
                {showArchiveButton &&
                    <Button
                        variant="secondary"
                        disabled={archiveDisabled}
                        className={archiveDisabled ? styles.DeleteButtonDisabled : styles.DeleteButton}
                        onClick={onArchiveClickHandler}
                    >
                        <FontAwesomeIcon icon="fa-solid fa-box-archive" style={{ color: archiveDisabled ? '#B9B9B9' : 'black' }} />
                        Archive
                    </Button>
                }
                {showAddButton &&
                    (dropdownOptions.length >= 1 ?
                        <Dropdown>
                            <Dropdown.Toggle
                                variant="secondary"
                                disabled={addDisabled}
                                className={addDisabled ? styles.AddContentButtonDisabled : styles.AddContentButton}
                            >
                                {addButtonLabel}
                            </Dropdown.Toggle>
                            <Dropdown.Menu className={styles.AddDropdownMenu}>
                                {dropdownOptions.map((option, index) => (
                                    <Dropdown.Item
                                        key={index}
                                        className={styles.AddDropdownItem}
                                        onClick={() => {
                                            if (addButtonLabel === 'Enroll Patient') {
                                                Logger.shared.info(
                                                    LogElements.enrollPatientButton,
                                                    LogAction.clicked,
                                                    'FilterManagementView.js',
                                                    'Start'
                                                );
                                            }
                                            onAddHandler(option)
                                        }}
                                    >
                                        {isAdmin ?
                                            `${option.name} - ${option.organizationName}`
                                            :
                                            `${option.name}`
                                        }
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                        :
                        <Button
                            variant="secondary"
                            className={addDisabled ? styles.AddContentButtonDisabled : styles.AddContentButton}
                            disabled={addDisabled}
                            onClick={onAddClickHandler}
                        >
                            {addButtonLabel}
                        </Button>)
                }
                <PerinHealthModal
                    modalId={"FilterManagementView"}
                    handleButtonClick={handleDialogButtonClick}
                />
                <PerinHealthModal
                    modalId={"FilterManagementViewArchive"}
                    handleButtonClick={handleArchiveDialogButtonClick}
                />
            </div>
        </div>
    );
};

export default FilterManagementView;
